package org.teddapp.project.ui.habits

import androidx.compose.runtime.Composable
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import com.tedd.db.Craving_observation
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.launch
import org.teddapp.project.core.CoreFacts
import org.teddapp.project.core.TeddIdentity
import org.teddapp.project.core.CoreSession
import org.teddapp.project.core.LocalCoreSession
import org.teddapp.project.ui.TeddDates
import org.teddapp.project.db.TeddRepositories

/** Zones corporelles proposées pour décrire une envie. Plus la description est fine, mieux c'est. */
internal val BODY_ZONES = listOf("gorge", "poitrine", "ventre", "mains", "chaleur", "tête")

/** Étapes du parcours « J'ai une envie ». */
internal enum class CravingStep { Idle, Naming, Describing, Guidance }

/** Compteurs par habitude : les deux montent, toujours. */
internal data class HabitTally(
    val habit: String,
    val observations: Int,
    val crossings: Int,
    /** Journées tenues sans envie ressentie. */
    val validations: Int = 0,
) {
    /** Indice de désenchantement : la part d'envies traversées. */
    val crossingRate: Int get() = if (observations == 0) 0 else (crossings * 100) / observations
}

/**
 * État du pilier Habitudes. Un seul point d'entrée : « J'ai une envie ». On nomme, on décrit,
 * on note l'intensité, on dit ce qui s'est passé, puis une consigne somatique s'affiche.
 */
internal class HabitsModel(
    private val repos: TeddRepositories,
    private val scope: CoroutineScope,
    private val core: CoreSession? = null,
) {
    var observations by mutableStateOf<List<Craving_observation>>(emptyList())

    var step by mutableStateOf(CravingStep.Idle)
    var habit by mutableStateOf("")
    var sensations by mutableStateOf("")
    var zones by mutableStateOf<Set<String>>(emptySet())
    var intensity by mutableStateOf(5)
    var lastCrossed by mutableStateOf(true)

    /** Habitudes déjà nommées, les plus observées d'abord. */
    val knownHabits: List<String>
        get() = observations.groupingBy { it.habit }.eachCount().entries.sortedByDescending { it.value }.map { it.key }

    val tallies: List<HabitTally>
        get() = observations.groupBy { it.habit }.map { (name, list) ->
            // Une journée tenue n'est pas une envie : elle se compte à part.
            val cravings = list.filter { it.outcome != "validated" }
            HabitTally(
                habit = name,
                observations = cravings.size,
                crossings = cravings.count { it.outcome == "crossed" },
                validations = list.count { it.outcome == "validated" },
            )
        }.sortedByDescending { it.observations + it.validations }

    val totalObservations: Int get() = observations.count { it.outcome != "validated" }
    val totalCrossings: Int get() = observations.count { it.outcome == "crossed" }

    /** Habitude déjà validée aujourd'hui : on ne la valide pas deux fois. */
    fun validatedToday(habit: String): Boolean =
        observations.any { it.habit == habit && it.outcome == "validated" && it.date == TeddDates.todayIso }

    /** Journée tenue : rien à traverser, mais le fait compte. */
    fun validate(habit: String) {
        if (validatedToday(habit)) return
        scope.launch { repos.cravings.validate(habit) }
        core?.record(TeddIdentity.USER_ID, CoreFacts.habitValidated(habit))
    }

    /** Zone dominante de la description en cours : elle choisit la consigne. */
    val dominantZone: String? get() = zones.firstOrNull()

    val canSubmit: Boolean get() = habit.isNotBlank()

    fun startCraving() {
        habit = ""; sensations = ""; zones = emptySet(); intensity = 5
        step = CravingStep.Naming
    }

    fun pickHabit(name: String) {
        habit = name
        step = CravingStep.Describing
    }

    fun toggleZone(zone: String) {
        zones = if (zone in zones) zones - zone else zones + zone
    }

    /** Enregistre l'observation puis affiche la consigne. Céder et traverser sont à égalité. */
    fun record(crossed: Boolean) {
        if (!canSubmit) return
        lastCrossed = crossed
        val h = habit
        val s = sensations
        val z = zones.toList()
        val i = intensity
        scope.launch { repos.cravings.record(habit = h, sensations = s, zones = z, intensity = i, crossed = crossed) }
        // Le noyau reçoit le fait : il en tire la consigne somatique et les compteurs.
        core?.record(
            userId = TeddIdentity.USER_ID,
            eventJson = CoreFacts.cravingObserved(
                habit = h,
                sensations = listOf(s, z.joinToString(", ")).filter { it.isNotBlank() }.joinToString(" · "),
                yielded = !crossed,
                intensity = i,
            ),
        )
        step = CravingStep.Guidance
    }

    fun cancel() { step = CravingStep.Idle }
    fun finish() { step = CravingStep.Idle }
}

@Composable
internal fun rememberHabitsModel(repos: TeddRepositories): HabitsModel {
    val scope = rememberCoroutineScope()
    val core = LocalCoreSession.current
    val model = remember(repos, core) { HabitsModel(repos, scope, core) }
    model.observations = repos.cravings.observeAll().collectAsState(emptyList()).value
    return model
}

/**
 * Consigne somatique ciblée sur la zone dominante. Le noyau la produira par mots-clés puis par
 * embeddings ; en local, un texte par zone suffit et dit la même chose.
 */
internal fun somaticGuidance(zone: String?): Pair<String, String> = when (zone) {
    "gorge" -> "Contraction laryngée · pulsion de déglutition" to
        "Pose ta conscience sur ta gorge. Sens la tension comme un objet physique. Laisse ta salive descendre lentement, sans forcer."
    "poitrine" -> "Serrement thoracique · souffle court" to
        "Pose une main sur ta poitrine. Observe où le serrement commence et où il s'arrête. Laisse l'air entrer sous la main, sans l'aider."
    "ventre" -> "Creux abdominal · faim apparente" to
        "Pose ton attention au creux du ventre. Regarde la sensation comme une vague : elle monte, elle atteint un sommet, elle redescend."
    "mains" -> "Agitation périphérique · besoin de saisir" to
        "Ouvre les mains, paumes vers le haut. Sens le fourmillement dans les doigts. Ne serre rien pendant trois respirations."
    "chaleur" -> "Montée thermique · afflux" to
        "Repère où la chaleur est la plus forte. Suis son bord, comme on suit le contour d'une tache. Elle change de forme quand on la regarde."
    "tête" -> "Rumination · pensée qui insiste" to
        "Nomme la pensée à voix basse : « voilà l'envie qui parle ». Reviens au poids de ton corps sur le siège, trois fois de suite."
    else -> "Observer, sans agir" to
        "Assieds-toi. Cherche où l'envie se sent dans le corps, exactement. Décris-la en silence pendant trois respirations : elle change toute seule."
}
